import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  ClipboardList, Search, CheckCircle, XCircle, Eye, User, Car, FileText, 
  Download, ExternalLink, CreditCard, Building, MessageSquare, RotateCcw, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Document label mapping
const DOCUMENT_LABELS = {
  'owner_photo': 'Owner Photo (Waist Up)',
  'owner_id_doc': 'ID Document (Copy)',
  'driver_license': "Driver's License",
  'proof_of_address': 'Proof of Address',
  'proof_of_bank_account': 'Proof of Bank Account',
  'bank_confirmation_letter': 'Bank Confirmation Letter',
  'driver_photo': 'Driver Photo (Waist Up)',
  'assistant_photo': 'Assistant Photo (Waist Up)',
  'vehicle_photo_front': 'Vehicle FRONT (Numberplate)',
  'vehicle_photo_left': 'Vehicle LEFT SIDE',
  'vehicle_photo_right': 'Vehicle RIGHT SIDE',
  'vehicle_photo_back': 'Vehicle BACK (Canopy)',
  'vehicle_images': 'Vehicle Images',
  'tracker_certificate': 'Tracker Certificate',
  'criminal_check': 'Criminal Check',
  'driver_pdp': 'PDP Document'
};

export default function AdminRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedReg, setSelectedReg] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  }, [activeTab]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const response = await axios.get(`${API_URL}/registrations${params}`);
      setRegistrations(response.data);
    } catch (error) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (regId) => {
    setProcessing(true);
    try {
      const response = await axios.put(`${API_URL}/registrations/${regId}/review`, {
        status: 'approved'
      });
      toast.success(`Registration approved! Temporary password: ${response.data.temporary_password}. Driver notified!`);
      fetchRegistrations();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to approve registration');
    } finally {
      setProcessing(false);
    }
  };

  const handlePreviewDocument = (regId, docName) => {
    setPreviewUrl(`${API_URL}/documents/registration/${regId}_${docName}/preview`);
    setPreviewTitle(DOCUMENT_LABELS[docName] || docName.replace(/_/g, ' '));
    setShowPreviewDialog(true);
  };

  const handleDownloadDocument = async (regId, docName) => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/registration/${regId}_${docName}/preview`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/registrations/${selectedReg.id}/review`, {
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      toast.success('Registration rejected');
      setShowRejectDialog(false);
      setShowDetailDialog(false);
      setRejectionReason('');
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to reject registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevertToPending = async (regId) => {
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/registrations/${regId}/review`, {
        status: 'pending'
      });
      toast.success('Registration reverted to pending - driver can now be reviewed again');
      fetchRegistrations();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to revert registration');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredRegistrations = registrations.filter(reg =>
    reg.owner_full_name.toLowerCase().includes(search.toLowerCase()) ||
    reg.owner_id_number.includes(search)
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-registrations">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Driver Registrations</h1>
          <p className="text-muted-foreground mt-1">Review and manage new driver applications</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or ID number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-registrations-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Registrations Table */}
        <Card>
          <CardHeader>
            <CardTitle>Registrations</CardTitle>
            <CardDescription>
              {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredRegistrations.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">No registrations found</h3>
                <p className="text-muted-foreground">
                  {activeTab === 'pending' ? 'No pending registrations' : 'No registrations in this category'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Owner Name</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Vehicles</TableHead>
                      <TableHead>Docs</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRegistrations.map((reg) => (
                      <TableRow key={reg.id} data-testid={`registration-row-${reg.id}`}>
                        <TableCell className="font-medium">{reg.owner_full_name}</TableCell>
                        <TableCell>{reg.owner_id_number}</TableCell>
                        <TableCell>{reg.owner_contact_number}</TableCell>
                        <TableCell>{reg.vehicles?.length || 0}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {reg.documents ? Object.keys(reg.documents).length : 0} files
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(reg.registration_status)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(reg.submitted_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedReg(reg);
                                setShowDetailDialog(true);
                              }}
                              data-testid={`view-registration-${reg.id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            {reg.registration_status === 'rejected' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevertToPending(reg.id)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Revert to Pending"
                                data-testid={`revert-${reg.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Registration Details</DialogTitle>
              <DialogDescription>
                Submitted on {selectedReg && new Date(selectedReg.submitted_at).toLocaleString()}
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="max-h-[65vh] pr-4">
              {selectedReg && (
                <div className="space-y-6 py-4">
                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Status:</span>
                    {getStatusBadge(selectedReg.registration_status)}
                    {selectedReg.rejection_reason && (
                      <span className="text-sm text-red-600 ml-2">
                        Reason: {selectedReg.rejection_reason}
                      </span>
                    )}
                  </div>

                  {/* Owner Details */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <User className="h-5 w-5 text-slate-600" />
                      Owner Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <InfoRow label="OWNER NAME" value={selectedReg.owner_full_name} />
                      <InfoRow label="OWNER ID NUMBER" value={selectedReg.owner_id_number} />
                      <InfoRow label="OWNER CELL" value={selectedReg.owner_contact_number} />
                      <InfoRow label="COMPANY TRADING AS" value={selectedReg.company_trading_as} />
                      <InfoRow label="OWNER ADDRESS" value={selectedReg.owner_address} className="col-span-2" />
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <CreditCard className="h-5 w-5 text-slate-600" />
                      Bank Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <InfoRow label="BANK NAME" value={selectedReg.bank_name} />
                      <InfoRow label="ACCOUNT NUMBER" value={selectedReg.account_number} />
                      <InfoRow label="BRANCH CODE" value={selectedReg.branch_code} />
                      <InfoRow label="ACCOUNT TYPE" value={selectedReg.account_type} />
                    </div>
                  </div>

                  {/* Next of Kin */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <Building className="h-5 w-5 text-slate-600" />
                      Next of Kin
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <InfoRow label="Name" value={selectedReg.nok_name} />
                      <InfoRow label="Relationship" value={selectedReg.nok_relationship} />
                      <InfoRow label="Contact" value={selectedReg.nok_contact} />
                    </div>
                  </div>

                  {/* Vehicles */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <Car className="h-5 w-5 text-slate-600" />
                      Vehicles & Drivers ({selectedReg.vehicles?.length || 0})
                    </h3>
                    {selectedReg.vehicles?.map((vehicle, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 mt-3 border">
                        <h4 className="font-medium mb-3">Vehicle & Driver {index + 1}</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <InfoRow label="VEHICLE TYPE" value={vehicle.vehicle_make_model} />
                          <InfoRow label="REGISTRATION NUMBER" value={vehicle.vehicle_registration} />
                          <InfoRow label="VEHICLE COMMENTS" value={vehicle.vehicle_comments} className="col-span-2" />
                          <Separator className="col-span-2 my-2" />
                          <InfoRow label="DRIVER NAME" value={vehicle.driver_full_name} />
                          <InfoRow label="DRIVER ID NUMBER" value={vehicle.driver_id_number} />
                          <InfoRow label="DRIVER CONTACT NUMBER" value={vehicle.driver_contact_number} />
                          <InfoRow label="DRIVER ADDRESS" value={vehicle.driver_address} />
                          <InfoRow label="DRIVERS LICENSE" value={vehicle.driver_license} />
                          <InfoRow label="LICENSE DISC" value={vehicle.license_disc} />
                          <InfoRow label="PDP" value={vehicle.pdp} />
                          <InfoRow label="CRIMINAL CHECKS" value={vehicle.criminal_checks} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Comments */}
                  {(selectedReg.finance_comments || selectedReg.general_comments) && (
                    <div className="border rounded-lg p-4 bg-slate-50">
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <MessageSquare className="h-5 w-5 text-slate-600" />
                        Comments
                      </h3>
                      <div className="space-y-3 text-sm">
                        {selectedReg.finance_comments && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 uppercase">Finance Comments</span>
                            <p className="text-slate-900">{selectedReg.finance_comments}</p>
                          </div>
                        )}
                        {selectedReg.general_comments && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 uppercase">General Comments</span>
                            <p className="text-slate-900">{selectedReg.general_comments}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents - WITH PREVIEW & DOWNLOAD */}
                  <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                    <h3 className="font-semibold flex items-center gap-2 mb-3 text-orange-800">
                      <FileText className="h-5 w-5" />
                      Uploaded Documents
                    </h3>
                    {selectedReg.documents && Object.keys(selectedReg.documents).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(selectedReg.documents).map(([docName, docPath]) => (
                          <div 
                            key={docName} 
                            className="bg-white rounded-lg p-3 border flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="h-5 w-5 text-orange-600" />
                              <span className="text-sm font-medium">
                                {DOCUMENT_LABELS[docName] || docName.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewDocument(selectedReg.id, docName)}
                                title="Preview"
                                data-testid={`preview-doc-${docName}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(selectedReg.id, docName)}
                                title="Download"
                                data-testid={`download-doc-${docName}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-orange-700">No documents uploaded</p>
                    )}
                  </div>

                  {/* Agreements */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <FileText className="h-5 w-5 text-slate-600" />
                      Agreements & Declarations
                    </h3>
                    <div className="space-y-2 text-sm">
                      <AgreementItem checked={selectedReg.criminal_check_consent} label="Criminal Background Check Consent" />
                      <AgreementItem checked={selectedReg.valid_pdp_required} label="Valid PDP Acknowledged" />
                      <AgreementItem checked={selectedReg.insurance_contribution_accept} label="Insurance Contribution Accepted (R399/vehicle)" />
                      <AgreementItem checked={selectedReg.tracking_device_active} label="Tracking Device Active" />
                      <AgreementItem checked={selectedReg.responsibility_acknowledgement} label="Responsibility Acknowledgement" />
                      <AgreementItem checked={selectedReg.weekly_records_acknowledgement} label="Weekly Records Acknowledgement" />
                    </div>
                  </div>

                  {/* Signature */}
                  {selectedReg.signature_data && (
                    <div className="border rounded-lg p-4 bg-slate-50">
                      <h3 className="font-semibold mb-3">Digital Signature</h3>
                      <div className="bg-white border rounded-lg p-4">
                        <img 
                          src={selectedReg.signature_data} 
                          alt="Signature" 
                          className="max-h-[100px] mx-auto"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            
            <DialogFooter className="border-t pt-4">
              {selectedReg?.registration_status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectDialog(true)}
                    disabled={processing}
                    data-testid="reject-registration-button"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedReg.id)}
                    disabled={processing}
                    data-testid="approve-registration-button"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {processing ? 'Processing...' : 'Approve'}
                  </Button>
                </>
              )}
              {selectedReg?.registration_status === 'rejected' && (
                <>
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => handleRevertToPending(selectedReg.id)}
                    disabled={processing}
                    data-testid="revert-to-pending-button"
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {processing ? 'Processing...' : 'Revert to Pending'}
                  </Button>
                </>
              )}
              {selectedReg?.registration_status === 'approved' && (
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Registration</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this registration.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="rejection_reason">Rejection Reason</Label>
              <Textarea
                id="rejection_reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={4}
                className="mt-2"
                data-testid="rejection-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={processing}
                data-testid="confirm-reject-button"
              >
                {processing ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{previewTitle}</DialogTitle>
            </DialogHeader>
            <div className="h-[70vh]">
              <iframe
                src={previewUrl}
                className="w-full h-full border rounded"
                title="Document Preview"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// Helper component for info rows
const InfoRow = ({ label, value, className = '' }) => (
  <div className={className}>
    <span className="text-xs font-medium text-slate-500 uppercase">{label}</span>
    <p className="text-slate-900">{value || '-'}</p>
  </div>
);

// Helper component for agreement items
const AgreementItem = ({ checked, label }) => (
  <div className="flex items-center gap-2">
    {checked ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600" />
    )}
    <span className={checked ? 'text-slate-900' : 'text-red-600'}>{label}</span>
  </div>
);
